import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ItineraryResponseDto, SpotResponseDto } from "@tabipla/shared";
import { cn } from "@tabipla/ui";
import { ItineraryDayCardContainer } from "./ItineraryDayCardContainer";
import type { ItineraryViewMode } from "./types";

interface ItineraryDayNavigatorProps {
  tripId: string;
  itineraries: ItineraryResponseDto[];
  viewMode?: ItineraryViewMode;
  onAddSpot: (itineraryId: string, startTime?: string) => void;
  onEditSpot: (spot: SpotResponseDto) => void;
}

/**
 * ItineraryDayNavigator provides swipe navigation between days
 * Similar to Google Calendar's mobile day view
 */
export function ItineraryDayNavigator({
  tripId,
  itineraries,
  viewMode = "list",
  onAddSpot,
  onEditSpot,
}: ItineraryDayNavigatorProps) {
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const sortedItineraries = [...itineraries].sort((a, b) => a.dayNumber - b.dayNumber);
  const totalDays = sortedItineraries.length;

  // Find today's itinerary index
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const todayIndex = sortedItineraries.findIndex((it) => it.date === today);
    if (todayIndex >= 0) {
      setCurrentDayIndex(todayIndex);
    }
  }, [sortedItineraries]);

  const goToPreviousDay = useCallback(() => {
    setCurrentDayIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextDay = useCallback(() => {
    setCurrentDayIndex((prev) => Math.min(totalDays - 1, prev + 1));
  }, [totalDays]);

  const goToDay = useCallback((index: number) => {
    setCurrentDayIndex(index);
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      // Only trigger swipe if horizontal movement is greater than vertical
      // and exceeds threshold
      const SWIPE_THRESHOLD = 50;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD) {
        if (deltaX > 0) {
          goToPreviousDay();
        } else {
          goToNextDay();
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
    },
    [goToPreviousDay, goToNextDay]
  );

  if (totalDays === 0) {
    return null;
  }

  const currentItinerary = sortedItineraries[currentDayIndex];

  return (
    <div className="space-y-3">
      {/* Day Navigation Header */}
      <div className="flex items-center justify-between rounded-lg bg-muted/50 p-2">
        {/* Previous Day Button */}
        <button
          type="button"
          onClick={goToPreviousDay}
          disabled={currentDayIndex === 0}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full transition-all",
            currentDayIndex === 0
              ? "text-muted-foreground/30"
              : "text-muted-foreground hover:bg-background hover:text-foreground active:scale-95"
          )}
          aria-label="前の日"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Day Indicator Dots */}
        <div className="flex items-center gap-1.5">
          {sortedItineraries.map((itinerary, index) => {
            const isToday = itinerary.date === new Date().toISOString().split("T")[0];
            return (
              <button
                key={itinerary.id}
                type="button"
                onClick={() => goToDay(index)}
                className={cn(
                  "flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-medium transition-all",
                  index === currentDayIndex
                    ? "bg-primary text-primary-foreground"
                    : isToday
                      ? "bg-primary/20 text-primary hover:bg-primary/30"
                      : "text-muted-foreground hover:bg-background"
                )}
                aria-label={`${itinerary.dayNumber}日目`}
              >
                {itinerary.dayNumber}
              </button>
            );
          })}
        </div>

        {/* Next Day Button */}
        <button
          type="button"
          onClick={goToNextDay}
          disabled={currentDayIndex === totalDays - 1}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full transition-all",
            currentDayIndex === totalDays - 1
              ? "text-muted-foreground/30"
              : "text-muted-foreground hover:bg-background hover:text-foreground active:scale-95"
          )}
          aria-label="次の日"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Current Day Content with Swipe */}
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="touch-pan-y"
      >
        <ItineraryDayCardContainer
          key={currentItinerary.id}
          tripId={tripId}
          itinerary={currentItinerary}
          viewMode={viewMode}
          onAddSpot={onAddSpot}
          onEditSpot={onEditSpot}
        />
      </div>

      {/* Swipe Hint (shown only on first visit) */}
      {totalDays > 1 && (
        <p className="text-center text-xs text-muted-foreground">
          ← 左右スワイプで日付を切り替え →
        </p>
      )}
    </div>
  );
}
